import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useChatShare } from '@features/components/Chat/contexts/ChatShareContext';
import { CustomMessageField } from '@features/components/Chat/constants';
import { ClinicalDataShareMessage } from '@features/components/Chat/components/ChatContent/Messages/types/chatMessages';
import { getClinicalDataShareMessage } from '@features/components/ClinicalData/components/ClinicalLog/sharedClinicalData.utils';
import { useAppContext } from '@features/contexts/AppProvider';
import { ClinicalDataToShare, ClinicalItem, ClinicalItemLog, WithPerfusionTime } from './types';

type UsePreviewFileType = {
  isPreviewOpen: boolean;
  previewFileId: string;
  handlePreviewOpen: (id: string) => void;
  handlePreviewClose: () => void;
};

export const usePreviewFile = (): UsePreviewFileType => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFileId, setPreviewFileId] = useState<string>('');

  const handlePreviewOpen = (fileId: string) => {
    if (fileId) {
      setPreviewFileId(fileId);
      setIsPreviewOpen(true);
    } else {
      toast.error('No media available to preview.');
    }
  };

  const handlePreviewClose = () => {
    setPreviewFileId('');
    setIsPreviewOpen(false);
  };

  return { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose };
};

export function useShareClinicalData(
  recordOrItem: WithPerfusionTime<ClinicalItemLog | ClinicalItem>,
  isTrialCase: boolean,
) {
  const { setPendingShare } = useChatShare();
  const {
    clinicalLog: { names },
  } = useAppContext();

  const recordRef = useRef(recordOrItem);
  recordRef.current = recordOrItem;

  return useCallback(
    (activeReading?: ClinicalDataToShare) => {
      const formattedMessage: ClinicalDataShareMessage = getClinicalDataShareMessage({
        record: recordRef.current,
        activeReading,
        clinicalLogAppNames: names,
        isTrialCase,
      });

      setPendingShare({
        [CustomMessageField.ClinicalDataAttachment]: formattedMessage,
        clinicalItemType: recordRef.current.clinicalItemType,
      });
    },
    [names, isTrialCase, setPendingShare],
  );
}
